/**
 * The lifecycle tracker section: the LifecycleBand plus its contextual "next
 * action" control (SWOT flow or the standard NextActionButton) and the
 * viewer-permission wiring. Each control uses the same key as its matching
 * surface — skip/SWOT → accounts.admin, Approve RFC → workflow.approve_rfc,
 * CBS checklist → rfc.cbs_queue, date edits → tenant.access_all — so the band
 * never disagrees with the RFC tab or the API.
 *
 * Extracted from AccountHeader so the two portals can mount it where their
 * reference design puts it:
 *   - Client portals: inside the header card (AccountHeader), on every tab.
 *   - Internal portal: as a cbs-portal-style card at the top of the Overview
 *     tab (AccountDetail).
 *
 * The tracker stays mounted after go-live — groups move in and out of Active,
 * so the stages must remain viewable, movable (Move stage), and their dates
 * clearable. To keep it from overwhelming the page once Active, the band
 * defaults to its collapsed one-line summary in that phase.
 */

import React from 'react';
import type {LifecyclePhase, AccountsSubStage, AccountsSwotStatus} from '@api/generated/client';
import {useAuthStore} from '@common-web/stores/authStore';
import {usePermissions} from '@common-web/hooks/use-permissions';
import {LifecycleBand} from './LifecycleBand';
import {resolveLifecycleViewerCaps} from './lifecyclePermissions';
import {NextActionButton, type NextActionHandlers} from './NextActionButton';
import {SwotControls} from './SwotControls';

/** Handlers backing the SWOT-call controls (Synrgy-only Prospect sub-stage). */
export interface SwotHandlers {
  onRequest: () => void;
  onSkip: () => void;
  onRequestSkip: () => void;
  onSchedule: (date: string) => void;
  onComplete: () => void;
  onCancel: () => void;
}

export interface AccountLifecycleSectionProps {
  account: {
    id: string;
    companyName: string;
    lifecyclePhase: LifecyclePhase;
    subStage: AccountsSubStage;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
    swotStatus: AccountsSwotStatus | null;
    swotScheduledDate: Date | string | null;
    isArchived: boolean;
    createdAt?: Date | string | null;
    stageTimeline?: {
      enteredAt: Record<string, string>;
      leftAt: Record<string, string>;
      skipped?: Record<string, {reason: string; at: string; by: string | null}>;
    } | null;
    stageDateOverrides?: Partial<Record<string, string>> | null;
  };
  hasRfc: boolean;
  /** Whether any MSA actually went out (envelope or manual record) — see NextActionButton. */
  msaSent: boolean;
  /** Open the Move-group modal — the one surface for manual lifecycle moves. */
  onEditLifecycle: () => void;
  isTransitioning?: boolean;
  /** Advance the account forward one sub-stage (inline "Skip" in the band),
   *  recording the required reason against the skipped stage. */
  onSkipSubStage?: (targetSubStage: AccountsSubStage, reason: string) => void;
  /** Sub-stages the caller may manually move to (drives "Move stage"). */
  editableSubStages?: AccountsSubStage[];
  /** Handlers backing the contextual "Next action" button in the band. */
  nextActionHandlers: NextActionHandlers;
  /** Whether the broker-approval sub-stage is part of this tenant's pipeline. */
  brokerApprovalEnabled?: boolean;
  /** Whether this account's tenant has the "Welcome Call" implementation step. */
  welcomeCallEnabled?: boolean;
  /** Edit a lifecycle step's date (internal analysts) — see LifecycleBand.
   *  `null` clears the step's date entirely. */
  onEditStageDate?: (subStage: AccountsSubStage, isoDate: string | null) => void;
  /** True when any next-action mutation is in flight. */
  isNextActionPending?: boolean;
  /** SWOT call (Synrgy-only). When enabled, the LEAD-phase next action becomes
   *  the SWOT request/schedule/complete flow instead of the proposal CTA. */
  swotEnabled?: boolean;
  swotHandlers?: SwotHandlers;
}

export function AccountLifecycleSection({
  account,
  hasRfc,
  msaSent,
  onEditLifecycle,
  isTransitioning = false,
  onSkipSubStage,
  editableSubStages = [],
  nextActionHandlers,
  brokerApprovalEnabled = false,
  welcomeCallEnabled = false,
  onEditStageDate,
  isNextActionPending = false,
  swotEnabled = false,
  swotHandlers,
}: AccountLifecycleSectionProps): React.ReactElement {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isBroker = useAuthStore((state) => state.isBrokerUser());
  const hasClientProfile = useAuthStore((state) => !!state.user?.clientProfile);
  const permissions = usePermissions();
  const caps = resolveLifecycleViewerCaps({
    isInternalUser,
    isBroker,
    hasClientProfile,
    permissions,
  });
  const {isInternalAdmin, isClientAdmin, canDriveCbs} = caps;
  // SWOT call (Synrgy-only) replaces the generic prospect next action while the
  // account is still on the Prospect/SWOT step (NEW_LEAD / SWOT_CALL): the broker
  // sets up / the admin runs the SWOT call, and once it's resolved the controls
  // surface "Generate RFC" to move into Onboarding (the proposal now comes after
  // RFC approval). Once an RFC exists the account moves on and the standard
  // next-action flow takes over. Brokers and admins may act; the controls
  // themselves gate the admin-only steps (schedule / complete / cancel).
  const showSwot =
    swotEnabled &&
    !!swotHandlers &&
    (account.subStage === 'NEW_LEAD' || account.subStage === 'SWOT_CALL') &&
    !account.isArchived;
  const nextActionNode = showSwot ? (
    <SwotControls
      companyName={account.companyName}
      status={account.swotStatus}
      scheduledDate={account.swotScheduledDate}
      isAdmin={caps.swotIsAdmin}
      canAct={isBroker || caps.swotIsAdmin}
      isPending={isNextActionPending || isTransitioning}
      onRequest={swotHandlers!.onRequest}
      onSkip={swotHandlers!.onSkip}
      onRequestSkip={swotHandlers!.onRequestSkip}
      onSchedule={swotHandlers!.onSchedule}
      onComplete={swotHandlers!.onComplete}
      onCancel={swotHandlers!.onCancel}
      onGenerateRfc={nextActionHandlers.onGenerateRfc}
      hasRfc={hasRfc}
    />
  ) : (
    <NextActionButton
      inline
      subStage={account.subStage}
      isInternalAdmin={isInternalAdmin}
      isBroker={isBroker}
      isClientAdmin={isClientAdmin}
      canDriveCbs={canDriveCbs}
      isArchived={account.isArchived}
      isPending={isNextActionPending || isTransitioning}
      hasRfc={hasRfc}
      brokerApprovalEnabled={brokerApprovalEnabled}
      welcomeCallEnabled={welcomeCallEnabled}
      msaSent={msaSent}
      handlers={nextActionHandlers}
    />
  );

  return (
    <LifecycleBand
      account={account}
      editableSubStages={account.isArchived ? [] : editableSubStages}
      onEditLifecycle={onEditLifecycle}
      isTransitioning={isTransitioning}
      nextAction={nextActionNode}
      canSkip={caps.canSkip && !account.isArchived && !showSwot}
      onSkip={onSkipSubStage}
      welcomeCallEnabled={welcomeCallEnabled}
      onEditStageDate={caps.canEditStageDates ? onEditStageDate : undefined}
      defaultCollapsed={account.lifecyclePhase === 'ACTIVE'}
    />
  );
}
